import React, { useState, useEffect } from 'react';
import { Upload, Image, message, Spin, Button, Space, Tooltip, Popover } from 'antd';
import { UploadOutlined, DeleteOutlined, SwapOutlined, LoadingOutlined } from '@ant-design/icons';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';
import { ossApi } from '@/services/oss';
import { useAuthStore } from '@/store/auth';
import axios from 'axios';

interface MultiImageUploaderProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
  accept?: string;
  maxSize?: number;
  maxCount?: number;
}

const MultiImageUploader: React.FC<MultiImageUploaderProps> = ({
  value = [],
  onChange,
  disabled = false,
  accept = 'image/*',
  maxSize = 5,
  maxCount = 20,
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [uploadingCount, setUploadingCount] = useState(0);
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());
  const { token } = useAuthStore();

  useEffect(() => {
    const uniqueUrls = Array.from(new Set(value || []));
    const initialFileList = uniqueUrls.map((url, index) => ({
      uid: String(-index - 1),
      name: `image-${index}`,
      status: 'done' as const,
      url: url,
    }));
    setFileList(initialFileList);
    setLoadedImages(new Set());
  }, [value]);

  useEffect(() => {
    const preloadImage = (url: string) => {
      if (loadedImages.has(url)) {
        return;
      }

      const img = new window.Image();
      img.onload = () => {
        setLoadedImages(prev => {
          const updated = new Set(prev);
          updated.add(url);
          return updated;
        });
      };
      img.onerror = () => {
        setLoadedImages(prev => {
          const updated = new Set(prev);
          updated.add(url);
          return updated;
        });
      };
      img.src = url;
    };

    fileList.forEach(file => {
      if (file.url) {
        preloadImage(file.url);
      }
    });
  }, [fileList]);

  const uploadToOSS = async (file: File): Promise<string> => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      throw new Error('只能上传图片文件!');
    }

    const isLtMaxSize = file.size / 1024 / 1024 < maxSize;
    if (!isLtMaxSize) {
      throw new Error(`图片大小不能超过 ${maxSize}MB!`);
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(ossApi.uploadPublicUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && typeof response.data === 'object') {
        if ('error_code' in response.data) {
          if (response.data.error_code !== 0) {
            throw new Error(response.data.message || '上传失败');
          }
          return response.data.data.preview_url || response.data.data.file_path;
        } else {
          return response.data.preview_url || response.data.file_path;
        }
      }

      throw new Error('上传响应格式错误');
    } catch (error: any) {
      console.error('上传图片失败:', error);
      throw new Error(`图片上传失败: ${error.message || '网络错误'}`);
    }
  };

  const handleMultipleUpload = async (file: RcFile) => {
    if (fileList.length >= maxCount) {
      message.warning(`最多只能上传 ${maxCount} 张图片`);
      return false;
    }

    const fileKey = `${file.name}-${file.size}-${file.lastModified}`;
    if (uploadingFiles.has(fileKey)) {
      return false;
    }

    setUploadingFiles(prev => new Set(prev).add(fileKey));
    setLoading(true);
    setUploadingCount(prev => prev + 1);

    uploadToOSS(file)
      .then(uploadedUrl => {
        setFileList(currentList => {
          if (currentList.some(f => f.url === uploadedUrl)) {
            message.warning(`图片 ${file.name} 已存在`);
            return currentList;
          }

          const newFile: UploadFile = {
            uid: fileKey,
            name: file.name,
            status: 'done',
            url: uploadedUrl,
          };

          const newFileList = [...currentList, newFile];
          onChange?.(newFileList.map(f => f.url as string));
          return newFileList;
        });

        message.success(`图片 ${file.name} 上传成功`);
      })
      .catch(error => {
        message.error(error.message || '图片上传失败');
      })
      .finally(() => {
        setUploadingFiles(prev => {
          const updated = new Set(prev);
          updated.delete(fileKey);
          return updated;
        });
        setLoading(false);
        setUploadingCount(prev => Math.max(0, prev - 1));
      });

    return false;
  };

  const handleRemove = (uid: string) => {
    const newFileList = fileList.filter(f => f.uid !== uid);
    setFileList(newFileList);
    onChange?.(Array.from(new Set(newFileList.map(f => f.url as string))));
  };

  const handleReorder = (dragIndex: number, hoverIndex: number) => {
    const newFileList = [...fileList];
    const [draggedItem] = newFileList.splice(dragIndex, 1);
    newFileList.splice(hoverIndex, 0, draggedItem);
    setFileList(newFileList);
    onChange?.(Array.from(new Set(newFileList.map(f => f.url as string))));
  };

  return (
    <div className="multi-image-uploader">
      <Spin spinning={loading}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div className="flex flex-wrap gap-2">
            {fileList.map((file, index) => (
              <div
                key={file.uid}
                className="relative group"
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', String(index));
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
                  handleReorder(dragIndex, index);
                }}
              >
                <Popover
                  content={
                    <Image
                      src={file.url}
                      width={400}
                      style={{ objectFit: 'contain', maxWidth: '100%' }}
                      fallback="/images/default-icon.png"
                    />
                  }
                  trigger="hover"
                  placement="top"
                >
                  <div className="relative w-24 h-24 border border-gray-200 rounded overflow-hidden cursor-pointer">
                    {file.url && !loadedImages.has(file.url) && (
                      <div className="absolute inset-0 bg-gray-200 flex items-center justify-center z-10">
                        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                      </div>
                    )}
                    <Image
                      src={file.url}
                      width={96}
                      height={96}
                      style={{ objectFit: 'cover' }}
                      fallback="/images/default-icon.png"
                      preview={false}
                      loading="lazy"
                    />
                    {!disabled && (
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                        <Space size="small">
                          <Tooltip title="拖拽排序">
                            <Button
                              type="text"
                              size="small"
                              icon={<SwapOutlined />}
                              className="text-white opacity-0 group-hover:opacity-100"
                              onMouseDown={(e) => e.preventDefault()}
                            />
                          </Tooltip>
                          <Tooltip title="删除">
                            <Button
                              type="text"
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              className="text-white opacity-0 group-hover:opacity-100"
                              onClick={() => handleRemove(file.uid)}
                            />
                          </Tooltip>
                        </Space>
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-center text-xs py-0.5">
                      {index + 1}
                    </div>
                  </div>
                </Popover>
              </div>
            ))}
            {(!disabled && fileList.length < maxCount) && (
              <Upload
                showUploadList={false}
                beforeUpload={handleMultipleUpload}
                accept={accept}
                disabled={loading || uploadingCount > 0}
                multiple
              >
                <div className="w-24 h-24 border border-dashed border-gray-300 rounded flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                  {uploadingCount > 0 ? (
                    <>
                      <Spin size="small" />
                      <span className="text-xs text-gray-400 mt-1">上传中...</span>
                    </>
                  ) : (
                    <>
                      <UploadOutlined className="text-xl text-gray-400" />
                      <span className="text-xs text-gray-400 mt-1">添加图片</span>
                    </>
                  )}
                </div>
              </Upload>
            )}
          </div>
          <div className="text-xs text-gray-500">
            已上传 {fileList.length}/{maxCount} 张图片 | 支持拖拽排序 | 单张图片大小不超过 {maxSize}MB | 可一次选择多张图片
          </div>
        </Space>
      </Spin>
    </div>
  );
};

export default MultiImageUploader;
